import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { Formik, Form, Field, FieldProps, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useMasterData } from '../../../hooks/useMasterData';
import { StudentStatus, studentsApi } from './api';
import { STUDENT_FIELDS } from './constants';

// Local Layout Components
const PageContainer: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Box sx={{ p: 3, ...sx }}>{children}</Box>
);

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>{title}</Typography>
);

const Section: React.FC<{ title: string; children: React.ReactNode; defaultExpanded?: boolean; sx?: any }> = ({ title, children, defaultExpanded = false, sx }) => (
  <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 2, ...sx }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

const getFullUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
};

const NAME_REGEX = /^[A-Za-z][A-Za-z\s'.-]*$/;
const ADDRESS_TEXT_REGEX = /^[A-Za-z\s'.-]+$/;
const ALLOWED_DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx'];
const ALLOWED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const isAllowedDocument = (file: File) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mimeType = String(file.type || '').toLowerCase();
  return ALLOWED_DOCUMENT_EXTENSIONS.includes(ext) || ALLOWED_DOCUMENT_MIME_TYPES.includes(mimeType);
};

const PhotoUpload: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  folder: string;
  error?: string | boolean;
}> = ({ label, value, onChange, folder, error }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setUploading(true);
      try {
        const response = await studentsApi.uploadFile(file, folder);
        onChange(response.url);
      } catch (err) {
        alert('Photo upload failed');
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={getFullUrl(value)}
          sx={{ width: 100, height: 100, border: error ? '2px solid red' : '1px solid #ccc' }}
        >
          {!value && <CameraAltIcon />}
        </Avatar>
        {uploading && (
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        )}
      </Box>
      <Button
        variant="outlined"
        component="label"
        size="small"
        startIcon={<CloudUploadIcon />}
        disabled={uploading}
      >
        Upload
        <input type="file" hidden accept="image/*" onChange={handleFileChange} />
      </Button>
      {error && (
        <Typography variant="caption" color="error">
          {error as string}
        </Typography>
      )}
    </Box>
  );
};

interface StudentFormProps {
  initialValues?: any;
  onSubmit: (values: any) => Promise<void>;
  isEdit?: boolean;
}

const FormObserver: React.FC<{ isSubmitting: boolean; isValid: boolean; errors: any }> = ({ isSubmitting, isValid, errors }) => {
  useEffect(() => {
    if (isSubmitting && !isValid && Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isSubmitting, isValid, errors]);
  return null;
};

const StudentForm: React.FC<StudentFormProps> = ({ initialValues, onSubmit, isEdit }) => {
  const navigate = useNavigate();
  const { masters, loading: loadingMasters, fetchMasters, fetchSections: fetchSectionsApi } = useMasterData();
  const { classes, castes, categories, houses, religions, bloodGroups, examBoards, academicSessions, parentServiceTypes, familyRelations, documents: masterDocuments, nationalities, motherTongues, studentTypes, mediums, leavingReasons } = masters;
  const [sections, setSections] = useState<any[]>([]);
  const [autoAdmissionNumber, setAutoAdmissionNumber] = useState('');

  const validationSchema = React.useMemo(() => Yup.object({
    firstName: Yup.string().matches(NAME_REGEX, 'First Name should contain alphabets only').required('First Name is required'),
    middleName: Yup.string().matches(NAME_REGEX, { message: 'Middle Name should contain alphabets only', excludeEmptyString: true }).nullable(),
    lastName: Yup.string().matches(NAME_REGEX, 'Last Name should contain alphabets only').required('Last Name is required'),
    admissionNumber: Yup.string().required('Admission Number is required'),
    scholarNumber: Yup.string()
      .matches(/^[A-Za-z0-9]{5}$/, 'Scholar Number must be exactly 5 alphanumeric characters')
      .required('Scholar Number is required'),
    email: Yup.string().email('Invalid email').nullable(),
    phone: Yup.string().matches(/^\d{10}$/, { message: 'Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
    classId: Yup.string().required('Class is required'),
    sectionId: Yup.string().required('Section is required'),
    academicSessionId: Yup.string().required('Academic Session is required'),
    aadharNumber: Yup.string().matches(/^\d{12}$/, { message: 'Invalid Aadhar Number (must be 12 digits)', excludeEmptyString: true }).nullable(),
    admissionDate: Yup.date()
      .nullable()
      .test(
        'admission-date-in-session',
        'Admission date must be within selected academic session',
        function (value) {
          if (!value) return true;
          const sessionId = this.parent.academicSessionId;
          if (!sessionId) return true;
          const session = (academicSessions || []).find((s: any) => s.id === sessionId);
          if (!session?.startDate || !session?.endDate) return true;

          const admissionDate = new Date(value);
          const sessionStart = new Date(session.startDate);
          const sessionEnd = new Date(session.endDate);

          const admissionDay = new Date(admissionDate.getFullYear(), admissionDate.getMonth(), admissionDate.getDate()).getTime();
          const startDay = new Date(sessionStart.getFullYear(), sessionStart.getMonth(), sessionStart.getDate()).getTime();
          const endDay = new Date(sessionEnd.getFullYear(), sessionEnd.getMonth(), sessionEnd.getDate()).getTime();

          return admissionDay >= startDay && admissionDay <= endDay;
        }
      ),
    dateOfBirth: Yup.date().nullable(),
    placeOfBirth: Yup.string().matches(NAME_REGEX, { message: 'Place of Birth should contain alphabets only', excludeEmptyString: true }).nullable(),
    gender: Yup.string().nullable(),
    bloodGroupId: Yup.string().nullable(),
    examBoardId: Yup.string().nullable(),
    nationalityId: Yup.string().nullable(),
    motherTongueId: Yup.string().nullable(),
    studentTypeId: Yup.string().nullable(),
    casteId: Yup.string().nullable(),
    categoryId: Yup.string().nullable(),
    religionId: Yup.string().nullable(),
    houseId: Yup.string().nullable(),
    mediumId: Yup.string().nullable(),
    parent: Yup.object().shape({
      fatherName: Yup.string().matches(NAME_REGEX, { message: 'Father Name should contain alphabets only', excludeEmptyString: true }).nullable(),
      fatherMobile: Yup.string().matches(/^\d{10}$/, { message: 'Father Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      fatherOccupationId: Yup.string().nullable(),
      motherName: Yup.string().matches(NAME_REGEX, { message: 'Mother Name should contain alphabets only', excludeEmptyString: true }).nullable(),
      motherMobile: Yup.string().matches(/^\d{10}$/, { message: 'Mother Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      motherOccupationId: Yup.string().nullable(),
      guardianName: Yup.string().matches(NAME_REGEX, { message: 'Guardian Name should contain alphabets only', excludeEmptyString: true }).nullable(),
      guardianMobile: Yup.string().matches(/^\d{10}$/, { message: 'Guardian Mobile must be exactly 10 digits numeric', excludeEmptyString: true }).nullable(),
      guardianEmail: Yup.string().email('Invalid email').nullable(),
      guardianRelationId: Yup.string().nullable(),
    }),
    addresses: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required(),
        addressLine1: Yup.string().required('Address Line 1 is required'),
        city: Yup.string().matches(ADDRESS_TEXT_REGEX, { message: 'City should contain alphabets only', excludeEmptyString: true }).required('City is required'),
        district: Yup.string().matches(ADDRESS_TEXT_REGEX, { message: 'District should contain alphabets only', excludeEmptyString: true }).nullable(),
        state: Yup.string().matches(ADDRESS_TEXT_REGEX, { message: 'State should contain alphabets only', excludeEmptyString: true }).required('State is required'),
        pincode: Yup.string()
          .transform((value) => String(value ?? '').replace(/\D/g, '').slice(0, 6))
          .matches(/^\d{6}$/, 'Pincode must be 6 digits')
          .required('Pincode is required'),
      })
    ),
    previousSchool: Yup.object().shape({
      schoolName: Yup.string().required('Previous School Name is required'),
      boardId: Yup.string().nullable(),
      passingYear: Yup.number().nullable(),
      previousClass: Yup.string().nullable(),
      mediumId: Yup.string().nullable(),
      medium: Yup.string().nullable(),
      tcNumber: Yup.string().nullable(),
      tcDate: Yup.date().nullable(),
      result: Yup.string().nullable(),
    }).nullable(),
    isDeclared: Yup.boolean().oneOf([true], 'You must declare the information is correct'),
    studentPhotoUrl: Yup.string().nullable(),
    fatherPhotoUrl: Yup.string().nullable(),
    motherPhotoUrl: Yup.string().nullable(),
    isPermanentSameAsPresent: Yup.boolean(),
    documents: Yup.array().test('mandatory-docs', 'Missing mandatory documents', function (value) {
      if (!masterDocuments) return true;
      const uploadedDocIds = value?.map((d: any) => d.documentId) || [];
      const missingMandatory = masterDocuments
        .filter((d: any) => d.isMandatory && (d.applicableFor === 'STUDENT' || d.applicableFor === 'BOTH' || !d.applicableFor))
        .filter((d: any) => !uploadedDocIds.includes(d.id));
      
      if (missingMandatory.length > 0) {
        return this.createError({
          message: `Missing mandatory documents: ${missingMandatory.map((d: any) => d.name).join(', ')}`,
          path: 'documents',
        });
      }
      return true;
    }),
  }), [masterDocuments, isEdit, academicSessions]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  useEffect(() => {
    if (isEdit) return;
    (async () => {
      try {
        const res = await studentsApi.getNextAdmissionNumber();
        setAutoAdmissionNumber(res?.admissionNumber || '');
      } catch {
        // Keep form usable even if auto number API fails.
      }
    })();
  }, [isEdit]);

  const fetchSections = useCallback(async (classId: string) => {
    if (!classId) {
      setSections([]);
      return;
    }
    const data = await fetchSectionsApi(classId);
    setSections(data);
  }, [fetchSectionsApi]);

  useEffect(() => {
    if (initialValues?.classId) {
      fetchSections(initialValues.classId);
    }
  }, [initialValues, fetchSections]);

  if (loadingMasters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const defaultValues = {
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    placeOfBirth: '',
    gender: '',
    bloodGroup: '',
    bloodGroupId: initialValues?.bloodGroupId || (initialValues?.bloodGroupMaster?.id) || (initialValues?.bloodGroup && bloodGroups.find(bg => bg.name === initialValues.bloodGroup)?.id) || '',
    examBoardId: initialValues?.examBoardId || (initialValues?.examBoard?.id) || '',
    aadharNumber: '',
    status: StudentStatus.ACTIVE,
    classId: '',
    sectionId: '',
    academicSessionId: initialValues?.academicSessionId || academicSessions.find((s: any) => s.isActive)?.id || '',
    admissionNumber: autoAdmissionNumber || '',
    scholarNumber: '',
    admissionDate: new Date().toISOString().split('T')[0],
    mediumId: initialValues?.mediumId || initialValues?.medium?.id || '',
    rte: false,
    bpl: false,
    casteId: '',
    categoryId: '',
    religionId: '',
    houseId: '',
    nationalityId: '',
    motherTongueId: '',
    studentTypeId: '',
    parent: {
      fatherName: initialValues?.parent?.fatherName || '',
      fatherMobile: initialValues?.parent?.fatherMobile || '',
      fatherOccupationId: initialValues?.parent?.fatherOccupationId || '',
      fatherQualification: initialValues?.parent?.fatherQualification || '',
      fatherAnnualIncome: initialValues?.parent?.fatherAnnualIncome || '',
      motherName: initialValues?.parent?.motherName || '',
      motherMobile: initialValues?.parent?.motherMobile || '',
      motherOccupationId: initialValues?.parent?.motherOccupationId || '',
      motherQualification: initialValues?.parent?.motherQualification || '',
      motherAnnualIncome: initialValues?.parent?.motherAnnualIncome || '',
      guardianName: initialValues?.parent?.guardianName || '',
      guardianMobile: initialValues?.parent?.guardianMobile || '',
      guardianRelationId: initialValues?.parent?.guardianRelationId || '',
      guardianAddress: initialValues?.parent?.guardianAddress || '',
      guardianEmail: initialValues?.parent?.guardianEmail || '',
    },
    addresses: initialValues?.addresses || [
      { type: 'PRESENT', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', district: '', country: 'India' },
      { type: 'PERMANENT', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', district: '', country: 'India' }
    ],
    previousSchool: {
      schoolName: '',
      boardId: '',
      passingYear: '',
      previousClass: '',
      mediumId: '',
      medium: '',
      result: '',
      tcNumber: '',
      tcDate: '',
      leavingReasonId: '',
    },
    documents: [],
    remarks: '',
    isDeclared: false,
    studentPhotoUrl: '',
    fatherPhotoUrl: '',
    motherPhotoUrl: '',
    guardianPhotoUrl: '',
    isPermanentSameAsPresent: false,
  };

  const latestSessionRecord = Array.isArray(initialValues?.sessionRecords)
    ? [...initialValues.sessionRecords].sort((a: any, b: any) => {
        const ad = new Date(a?.academicSession?.startDate || 0).getTime();
        const bd = new Date(b?.academicSession?.startDate || 0).getTime();
        return bd - ad;
      })[0]
    : null;

  const latestAcademicRecord = Array.isArray(initialValues?.academicRecords)
    ? [...initialValues.academicRecords].sort((a: any, b: any) => {
        const ad = new Date(a?.academicSession?.startDate || 0).getTime();
        const bd = new Date(b?.academicSession?.startDate || 0).getTime();
        return bd - ad;
      })[0]
    : null;

  const mergedValues = {
    ...defaultValues,
    ...(initialValues || {}),
    academicSessionId:
      initialValues?.academicSessionId ||
      latestSessionRecord?.academicSessionId ||
      latestSessionRecord?.academicSession?.id ||
      latestAcademicRecord?.academicSessionId ||
      latestAcademicRecord?.academicSession?.id ||
      defaultValues.academicSessionId,
    // force flat ids and preserve booleans
    bloodGroupId: initialValues?.bloodGroupId || initialValues?.bloodGroup?.id || initialValues?.bloodGroupMaster?.id || '',
    examBoardId: initialValues?.examBoardId || initialValues?.examBoard?.id || '',
    mediumId: initialValues?.mediumId || initialValues?.medium?.id || '',
    casteId: initialValues?.casteId || initialValues?.caste?.id || '',
    categoryId: initialValues?.categoryId || initialValues?.category?.id || '',
    religionId: initialValues?.religionId || initialValues?.religion?.id || '',
    houseId: initialValues?.houseId || initialValues?.house?.id || '',
    nationalityId: initialValues?.nationalityId || initialValues?.nationality?.id || '',
    motherTongueId: initialValues?.motherTongueId || initialValues?.motherTongue?.id || '',
    studentTypeId: initialValues?.studentTypeId || initialValues?.studentType?.id || '',
    isDeclared: initialValues?.isDeclared === true,
    isPermanentSameAsPresent: initialValues?.isPermanentSameAsPresent === true,
    documents: (initialValues?.documents || []).map((d: any) => ({
      ...d,
      documentId: d.documentId || d.id || d.document?.id,
      name: d.name || d.document?.name || '',
      url: d.url || d.filePath || d.document?.url || '',
    })),
    previousSchool: {
      ...defaultValues.previousSchool,
      ...(initialValues?.previousSchool || {}),
      boardId: initialValues?.previousSchool?.boardId || initialValues?.previousSchool?.board?.id || '',
      mediumId: initialValues?.previousSchool?.mediumId || initialValues?.previousSchool?.medium?.id || '',
      leavingReasonId: initialValues?.previousSchool?.leavingReasonId || initialValues?.previousSchool?.leavingReason?.id || '',
    },
  };

  const handleSameAsPresentChange = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any, values: any) => {
      const isChecked = e.target.checked;
      setFieldValue('isPermanentSameAsPresent', isChecked);
      if (isChecked) {
          const presentAddr = values.addresses.find((a: any) => a.type === 'PRESENT');
          if (presentAddr) {
              const newAddresses = values.addresses.map((a: any) => 
                  a.type === 'PERMANENT' ? { ...presentAddr, type: 'PERMANENT' } : a
              );
              setFieldValue('addresses', newAddresses);
          }
      }
  };

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
    index: number, 
    fieldName: string, 
    setFieldValue: any,
    setFieldTouched: any,
    values: any
  ) => {
    let value = e.target.value;
    const addressType = values.addresses[index].type;

    if (fieldName === 'city' || fieldName === 'district' || fieldName === 'state') {
      value = value.replace(/[^A-Za-z\s'.-]/g, '');
    }
    if (fieldName === 'pincode') {
      value = value.replace(/\D/g, '').slice(0, 6);
    }
    
    // Update and mark touched so validation messages can appear while typing.
    setFieldValue(`addresses.${index}.${fieldName}`, value);
    setFieldTouched(`addresses.${index}.${fieldName}`, true, false);
    
    // If it's PRESENT address and sync is on, update PERMANENT address
    if (addressType === 'PRESENT' && values.isPermanentSameAsPresent) {
        const permIndex = values.addresses.findIndex((a: any) => a.type === 'PERMANENT');
        if (permIndex !== -1) {
            setFieldValue(`addresses.${permIndex}.${fieldName}`, value);
            setFieldTouched(`addresses.${permIndex}.${fieldName}`, true, false);
        }
    }
  };

  return (
    <PageContainer>
      <PageHeader title={isEdit ? "Edit Student" : "Create Student"} />
      
      <Formik
        initialValues={mergedValues}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          try {
            await onSubmit(values);
          } finally {
            actions.setSubmitting(false);
          }
        }}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched, isSubmitting, getFieldProps, submitForm, isValid }) => {
             // Render Field Helper
            const renderFormField = (fieldDefinition: any, prefix = '') => {
                const isRelation = fieldDefinition.isRelation;
                const baseName = fieldDefinition.name;
                
                // Map base name to form field name
                let fieldName = baseName;
                if (isRelation && !baseName.endsWith('Id')) fieldName = `${baseName}Id`;
                
                if (baseName === 'bloodGroup' || baseName === 'bloodGroupMaster') fieldName = 'bloodGroupId'; // Special mapping
                if (baseName === 'leavingReason') fieldName = 'leavingReasonId';
                if (baseName === 'examBoard') fieldName = 'examBoardId';
                if (baseName === 'board' && prefix === 'previousSchool') fieldName = 'boardId';

                const fullName = prefix ? `${prefix}.${fieldName}` : fieldName;
                const label = fieldDefinition.label;
                const grid = fieldDefinition.grid;
                
                // Error handling
                // For nested fields like parent.fatherName, touched/errors are nested
                let error = (errors as any)[fullName];
                let isTouched = (touched as any)[fullName];
                
                if (prefix) {
                    const prefixError = (errors as any)[prefix];
                    const prefixTouched = (touched as any)[prefix];
                    if (prefixError) error = prefixError[fieldName];
                    if (prefixTouched) isTouched = prefixTouched[fieldName];
                }

                const hasError = Boolean(isTouched && error);

                // 1. Boolean Fields (Yes/No Select)
                if (fieldDefinition.type === 'boolean') {
                    const val = prefix ? (values as any)[prefix]?.[fieldName] : (values as any)[fullName];
                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <FormControl fullWidth error={hasError}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                        name={fullName}
                                        value={val === true ? 'true' : val === false ? 'false' : ''}
                                        label={label}
                                        onChange={(e) => setFieldValue(fullName, e.target.value === 'true')}
                                    >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    );
                }

                // 2. Select Fields (Relations & Enums)
                if (isRelation || baseName === 'gender' || baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster' || baseName === 'examBoard' || baseName === 'examBoardId' || baseName === 'studentType' || baseName === 'medium' || baseName === 'board') {
                    let options: any[] = [];
                    const val = prefix ? (values as any)[prefix]?.[fieldName] : (values as any)[fullName];
                    
                    if (baseName === 'gender') options = [{id: 'MALE', name: 'Male'}, {id: 'FEMALE', name: 'Female'}, {id: 'OTHER', name: 'Other'}];
                    else if (baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster') options = bloodGroups;
                    else if (baseName === 'examBoard' || baseName === 'examBoardId') options = examBoards;
                    else if (baseName === 'caste') options = castes;
                    else if (baseName === 'category') options = categories;
                    else if (baseName === 'religion') options = religions;
                    else if (baseName === 'house') options = houses;
                    else if (baseName === 'nationality') options = nationalities;
                    else if (baseName === 'motherTongue') options = motherTongues;
                    else if (baseName === 'studentType') options = studentTypes;
                    else if (baseName === 'medium') options = mediums;
                    else if (baseName === 'class') options = classes;
                    else if (baseName === 'section') options = sections;
                    else if (baseName === 'fatherOccupation' || baseName === 'motherOccupation') options = parentServiceTypes;
                    else if (baseName === 'guardianRelation') options = familyRelations;
                    else if (baseName === 'leavingReason') options = leavingReasons;
                    else if (baseName === 'board') options = examBoards;

                    // Special Change Handlers
                    const onChange = (e: any) => {
                        handleChange(e);
                        if (baseName === 'class') fetchSections(e.target.value);
                        if (baseName === 'bloodGroup' || baseName === 'bloodGroupId' || baseName === 'bloodGroupMaster') {
                            const selected = bloodGroups.find(bg => bg.id === e.target.value);
                            if (selected) setFieldValue('bloodGroup', selected.name);
                        }
                    };

                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <FormControl fullWidth error={hasError}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                    name={fullName}
                                    value={val || ''}
                                    label={label}
                                    onChange={onChange}
                                >
                                    {options?.map((opt) => (
                                      <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.reason || opt.code || 'Unnamed'}</MenuItem>
                                    ))}
                                </Select>
                                {hasError && <FormHelperText>{error as string}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    );
                }

                // 3. Date Fields
                if (fieldDefinition.type === 'date') {
                    return (
                        <Grid item xs={12} md={grid} key={fullName}>
                            <TextField
                                name={fullName}
                                label={label}
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={prefix ? ((values as any)[prefix] || {})[fieldName] || '' : (values as any)[fullName] || ''}
                                onChange={handleChange}
                                error={hasError}
                                helperText={hasError ? (error as string) : ''}
                            />
                        </Grid>
                    );
                }

                // 4. Default Text Fields
                const isMobileField = ['phone', 'parent.fatherMobile', 'parent.motherMobile', 'parent.guardianMobile'].includes(fullName);
                const isAadhaarField = fullName === 'aadharNumber';
                const isNameField = [
                  'firstName',
                  'middleName',
                  'lastName',
                  'parent.fatherName',
                  'parent.motherName',
                  'parent.guardianName',
                  'placeOfBirth',
                ].includes(fullName);
                const isScholarField = fullName === 'scholarNumber';

                const currentValue = prefix ? ((values as any)[prefix] || {})[fieldName] || '' : (values as any)[fullName] || '';
                const shouldShowLiveError = Boolean(error) && (Boolean(isTouched) || ((isMobileField || isAadhaarField) && String(currentValue).length > 0));

                const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  let next = e.target.value;
                  if (isMobileField) {
                    next = next.replace(/\D/g, '').slice(0, 10);
                  }
                  if (isAadhaarField) {
                    next = next.replace(/\D/g, '').slice(0, 12);
                  }
                  if (isNameField) {
                    next = next.replace(/[^A-Za-z\s'.-]/g, '');
                  }
                  if (isScholarField) {
                    next = next.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 5);
                  }
                  setFieldValue(fullName, next);
                  if (isMobileField || isAadhaarField || isScholarField) {
                    setFieldTouched(fullName, true, false);
                  }
                };

                const textInputProps: InputBaseComponentProps | undefined = isMobileField
                  ? { maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }
                  : isAadhaarField
                    ? { maxLength: 12, inputMode: 'numeric', pattern: '[0-9]*' }
                    : isScholarField
                      ? { maxLength: 5, pattern: '[A-Za-z0-9]*' }
                    : undefined;

                return (
                    <Grid item xs={12} md={grid} key={fullName}>
                        <TextField
                            name={fullName}
                            label={label}
                            type={fieldDefinition.type === 'number' ? 'number' : 'text'}
                            fullWidth
                            value={currentValue}
                            onChange={handleTextChange}
                            inputProps={textInputProps}
                            InputProps={fullName === 'admissionNumber' ? { readOnly: true } : undefined}
                            error={shouldShowLiveError}
                            helperText={shouldShowLiveError ? (error as string) : ''}
                        />
                    </Grid>
                );
            };

            const formatValue = (v: any) => {
              if (v === null || v === undefined || v === '') return '-';
              if (typeof v === 'boolean') return v ? 'Yes' : 'No';
              return String(v);
            };

            const handleDownloadPdf = () => {
              const studentName = `${values.firstName || ''} ${values.middleName || ''} ${values.lastName || ''}`.replace(/\s+/g, ' ').trim() || 'Student';
              const fullName = studentName;
              const present = values.addresses?.find((a: any) => a.type === 'PRESENT') || {};
              const permanent = values.addresses?.find((a: any) => a.type === 'PERMANENT') || {};
              const docRows = (values.documents || [])
                .map((d: any) => `<tr><td style="padding:6px;border:1px solid #ddd;">${formatValue(d.name)}</td><td style="padding:6px;border:1px solid #ddd;">${formatValue(d.url)}</td></tr>`)
                .join('');

              const html = `
                <html>
                  <head>
                    <title>Student Form - ${fullName}</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 24px; color:#111; }
                      h1, h2 { margin: 0 0 10px 0; }
                      h2 { margin-top: 18px; font-size: 16px; }
                      .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; }
                      .item { border-bottom: 1px solid #eee; padding: 4px 0; }
                      .label { color:#555; font-size:12px; }
                      .val { font-size:14px; font-weight:600; }
                      table { width:100%; border-collapse: collapse; margin-top: 8px; }
                    </style>
                  </head>
                  <body>
                    <h1>Student Form</h1>
                    <div style="margin-bottom:8px;color:#444;">Generated on: ${new Date().toLocaleString()}</div>
                    <h2>Personal Details</h2>
                    <div class="grid">
                      <div class="item"><div class="label">Name</div><div class="val">${formatValue(fullName)}</div></div>
                      <div class="item"><div class="label">Admission No</div><div class="val">${formatValue(values.admissionNumber)}</div></div>
                      <div class="item"><div class="label">Scholar No</div><div class="val">${formatValue(values.scholarNumber)}</div></div>
                      <div class="item"><div class="label">Email</div><div class="val">${formatValue(values.email)}</div></div>
                      <div class="item"><div class="label">Phone</div><div class="val">${formatValue(values.phone)}</div></div>
                      <div class="item"><div class="label">Date of Birth</div><div class="val">${formatValue(values.dateOfBirth)}</div></div>
                    </div>
                    <h2>Academic Details</h2>
                    <div class="grid">
                      <div class="item"><div class="label">Academic Session</div><div class="val">${formatValue((academicSessions || []).find((s: any) => s.id === values.academicSessionId)?.name)}</div></div>
                      <div class="item"><div class="label">Admission Date</div><div class="val">${formatValue(values.admissionDate)}</div></div>
                    </div>
                    <h2>Parent Details</h2>
                    <div class="grid">
                      <div class="item"><div class="label">Father Name</div><div class="val">${formatValue(values.parent?.fatherName)}</div></div>
                      <div class="item"><div class="label">Mother Name</div><div class="val">${formatValue(values.parent?.motherName)}</div></div>
                      <div class="item"><div class="label">Father Mobile</div><div class="val">${formatValue(values.parent?.fatherMobile)}</div></div>
                      <div class="item"><div class="label">Mother Mobile</div><div class="val">${formatValue(values.parent?.motherMobile)}</div></div>
                    </div>
                    <h2>Address</h2>
                    <div class="grid">
                      <div class="item"><div class="label">Present City / District / State / Pincode</div><div class="val">${formatValue(present.city)} / ${formatValue(present.district)} / ${formatValue(present.state)} / ${formatValue(present.pincode)}</div></div>
                      <div class="item"><div class="label">Permanent City / District / State / Pincode</div><div class="val">${formatValue(permanent.city)} / ${formatValue(permanent.district)} / ${formatValue(permanent.state)} / ${formatValue(permanent.pincode)}</div></div>
                    </div>
                    <h2>Documents</h2>
                    <table>
                      <thead><tr><th style="text-align:left;padding:6px;border:1px solid #ddd;">Name</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">URL</th></tr></thead>
                      <tbody>${docRows || '<tr><td colspan="2" style="padding:6px;border:1px solid #ddd;">No documents</td></tr>'}</tbody>
                    </table>
                  </body>
                </html>
              `;

              const printWindow = window.open('', '_blank', 'width=1024,height=768');
              if (!printWindow) {
                alert('Unable to open print window. Please allow pop-ups.');
                return;
              }
              printWindow.document.open();
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
            };

            return (
          <Form>
            <FormObserver isSubmitting={isSubmitting} isValid={isValid} errors={errors} />
            <Card sx={{ p: 3, mb: 3 }}>
              {/* Personal Details Section */}
              <Section title="1. Personal Details" defaultExpanded>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <PhotoUpload
                      label="Student Photo"
                      value={values.studentPhotoUrl}
                     folder="students/studentPhoto"
                      onChange={(url) => setFieldValue('studentPhotoUrl', url)}
                     error={(isSubmitting || touched.studentPhotoUrl) ? (errors.studentPhotoUrl as string) : undefined}
                   />
                </Box>
                <Grid container spacing={2}>
                    {STUDENT_FIELDS.personal.map(field => renderFormField(field))}
                </Grid>
              </Section>

              {/* Academic Details Section */}
              <Section title="Academic Details">
                <Grid container spacing={2}>
                     <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Academic Session</InputLabel>
                            <Select
                                name="academicSessionId"
                                value={values.academicSessionId}
                                label="Academic Session"
                                disabled // Always disabled/read-only as it defaults to active
                            >
                                {(academicSessions || []).map((s: any) => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {STUDENT_FIELDS.academic.map(field => renderFormField(field))}
                </Grid>
              </Section>

              {/* Parent Details Section */}
              <Section title="Parent & Guardian Details">
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Father's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload
                          label="Father Photo"
                          value={values.fatherPhotoUrl}
                          folder="students/fatherPhoto"
                          onChange={(url) => setFieldValue('fatherPhotoUrl', url)}
                          error={(isSubmitting || touched.fatherPhotoUrl) ? (errors.fatherPhotoUrl as string) : undefined}
                        />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.father.map(field => renderFormField(field, 'parent'))}
                    </Grid>
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Mother's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload
                          label="Mother Photo"
                          value={values.motherPhotoUrl}
                          folder="students/motherPhoto"
                          onChange={(url) => setFieldValue('motherPhotoUrl', url)}
                          error={(isSubmitting || touched.motherPhotoUrl) ? (errors.motherPhotoUrl as string) : undefined}
                        />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.mother.map(field => renderFormField(field, 'parent'))}
                    </Grid>
                </Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Guardian's Details</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <PhotoUpload
                          label="Guardian Photo"
                          value={values.guardianPhotoUrl}
                          folder="students/guardianPhoto"
                          onChange={(url) => setFieldValue('guardianPhotoUrl', url)}
                        />
                    </Box>
                    <Grid container spacing={2}>
                        {STUDENT_FIELDS.parent.guardian.map(field => {
                            if (field.name === 'guardianAddress') return null; // Handle manually below if needed, or let generic handler do it (generic handles text field)
                            return renderFormField(field, 'parent');
                        })}
                        <Grid item xs={12}>
                             <TextField
                                name="parent.guardianAddress"
                                label="Guardian Address"
                                fullWidth
                                multiline
                                rows={2}
                                value={values.parent.guardianAddress}
                                onChange={handleChange}
                             />
                        </Grid>
                    </Grid>
                </Box>
              </Section>

              {/* Address Details Section */}
              <Section title="Address Details">
                <FormControlLabel
                    control={
                        <Checkbox 
                            name="isPermanentSameAsPresent"
                            checked={Boolean(values.isPermanentSameAsPresent)}
                            onChange={(e) => handleSameAsPresentChange(e, setFieldValue, values)}
                        />
                    }
                    label="Permanent Address is same as Present Address"
                    sx={{ mb: 2 }}
                 />
                 <Grid container spacing={4}>
                    {/* Present Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Present Address</Typography>
                        <Grid container spacing={2}>
                            {STUDENT_FIELDS.address.map((field) => (
                                <Grid item xs={12} md={field.grid === 12 ? 12 : 6} key={`present-${field.name}`}>
                                    <TextField 
                                        label={field.label}
                                        fullWidth
                                        value={(values.addresses[0] as any)[field.name]}
                                        onChange={(e) => handleAddressChange(e, 0, field.name, setFieldValue, setFieldTouched, values)}
                                  inputProps={field.name === 'pincode' ? { maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
                                        error={(touched.addresses as any)?.[0]?.[field.name] && Boolean((errors.addresses as any)?.[0]?.[field.name])}
                                        helperText={(touched.addresses as any)?.[0]?.[field.name] && (errors.addresses as any)?.[0]?.[field.name]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    
                    {/* Permanent Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Permanent Address</Typography>
                        <Grid container spacing={2}>
                            {STUDENT_FIELDS.address.map((field) => (
                                <Grid item xs={12} md={field.grid === 12 ? 12 : 6} key={`permanent-${field.name}`}>
                                    <TextField 
                                        label={field.label}
                                        fullWidth
                                        value={(values.addresses[1] as any)[field.name]}
                                        onChange={(e) => handleAddressChange(e, 1, field.name, setFieldValue, setFieldTouched, values)}
                                  inputProps={field.name === 'pincode' ? { maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
                                        disabled={values.isPermanentSameAsPresent}
                                        error={(touched.addresses as any)?.[1]?.[field.name] && Boolean((errors.addresses as any)?.[1]?.[field.name])}
                                        helperText={(touched.addresses as any)?.[1]?.[field.name] && (errors.addresses as any)?.[1]?.[field.name]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                 </Grid>
              </Section>

              {/* Previous School Details */}
              <Section title="Previous School Details">
                 <Grid container spacing={2}>
                    {STUDENT_FIELDS.previousSchool.map(field => renderFormField(field, 'previousSchool'))}
                 </Grid>
              </Section>

              {/* Documents Section */}
              <Section title="Documents Upload">
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>Document Name</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {masterDocuments
                        ?.filter((doc: any) => doc.applicableFor === 'STUDENT' || doc.applicableFor === 'BOTH' || !doc.applicableFor)
                        .map((masterDoc: any) => {
                          const uploadedDoc = values.documents.find((d: any) => d.documentId === masterDoc.id);
                          const isMandatory = masterDoc.isMandatory;

                          return (
                            <TableRow key={masterDoc.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">{masterDoc.name}</Typography>
                              </TableCell>
                              <TableCell>
                                {isMandatory ? <Chip label="Mandatory" color="error" size="small" /> : <Chip label="Optional" size="small" />}
                              </TableCell>
                              <TableCell>
                                {uploadedDoc ? (
                                  <Chip label="Uploaded" color="success" size="small" icon={<CloudUploadIcon />} />
                                ) : (
                                  <Chip label="Pending" color="warning" size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                {uploadedDoc ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      color="primary"
                                      href={getFullUrl(uploadedDoc.url)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {uploadedDoc.name || 'View File'}
                                    </Button>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => {
                                        const newDocs = values.documents.filter((d: any) => d.documentId !== masterDoc.id);
                                        setFieldValue('documents', newDocs);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CloudUploadIcon />}
                                  >
                                    Upload
                                    <input
                                      type="file"
                                      hidden
                                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                      onChange={async (e) => {
                                        if (e.target.files && e.target.files[0]) {
                                          try {
                                            const file = e.target.files[0];
                                            if (!isAllowedDocument(file)) {
                                              alert('Only PDF, DOC, and DOCX files are allowed.');
                                              e.target.value = '';
                                              return;
                                            }
                                            const response = await studentsApi.uploadFile(file, 'students/documents');
                                            const newDoc = {
                                              documentId: masterDoc.id,
                                              name: file.name,
                                              url: response.url
                                            };
                                            setFieldValue('documents', [...values.documents, newDoc]);
                                          } catch (err) {
                                            alert('Document upload failed');
                                          } finally {
                                            e.target.value = '';
                                          }
                                        }
                                      }}
                                    />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {(!masterDocuments || masterDocuments.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No documents configured in system.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {touched.documents && typeof errors.documents === 'string' && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {errors.documents}
                  </Typography>
                )}
              </Section>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isDeclared"
                      checked={Boolean(values.isDeclared)}
                      onChange={handleChange}
                    />
                  }
                  label="I hereby declare that the information provided is true and correct to the best of my knowledge."
                />
                {touched.isDeclared && errors.isDeclared && (
                  <FormHelperText error>{errors.isDeclared as string}</FormHelperText>
                )}
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDownloadPdf}
                >
                  Download PDF
                </Button>
                <Button variant="outlined" onClick={() => navigate('/scholar-register/students')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : (isEdit ? 'Update Student' : 'Create Student')}
                </Button>
              </Box>
            </Card>
          </Form>
        )}}
      </Formik>
    </PageContainer>
  );
};

export default StudentForm;
